import React, { useMemo } from "react";
import "./SalaryDistributionModal.css";

export default function SalaryDistributionModal({ roster, onClose }) {
  const salaryData = useMemo(() => {
    const SALARY_CAP = 200; // $200M per team

    // Parse contracts and calculate salaries
    const players = roster.map(player => {
      const contract = player.contract || "";
      const [yearsStr, salaryStr] = contract.split("/");
      const years = parseInt(yearsStr) || 0;
      const annual = parseFloat(salaryStr) || 0;
      const total = annual * years;

      return {
        name: player.displayName || player.name || "Unknown",
        position: player.position || "N/A",
        contract,
        years,
        annual,
        total,
      };
    });

    // Sort by annual salary (descending)
    const sorted = [...players].sort((a, b) => b.annual - a.annual);

    // Calculate totals
    const totalAnnual = players.reduce((sum, p) => sum + p.annual, 0);
    const totalContractValue = players.reduce((sum, p) => sum + p.total, 0);
    const avgAnnual = players.length > 0 ? totalAnnual / players.length : 0;
    const capPercentage = (totalAnnual / SALARY_CAP) * 100;

    return {
      players: sorted,
      totalAnnual,
      totalContractValue,
      avgAnnual,
      capPercentage,
      SALARY_CAP,
      playerCount: players.length,
    };
  }, [roster]);

  return (
    <div className="salary-overlay">
      <div className="salary-modal">
        {/* Header */}
        <div className="salary-header">
          <h2 className="salary-title">💰 Salary Distribution</h2>
          <button className="salary-close-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="salary-body">
          {/* Summary Stats */}
          <div className="salary-summary">
            <div className="summary-card">
              <span className="summary-label">Total Annual</span>
              <span className="summary-value">${salaryData.totalAnnual.toFixed(1)}M</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Cap Used</span>
              <span className="summary-value">{salaryData.capPercentage.toFixed(1)}%</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Avg Salary</span>
              <span className="summary-value">${salaryData.avgAnnual.toFixed(2)}M</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Players</span>
              <span className="summary-value">{salaryData.playerCount}</span>
            </div>
          </div>

          {/* Cap Bar */}
          <div className="cap-bar-section">
            <div className="cap-bar-label">
              Salary Cap: ${salaryData.SALARY_CAP}M
            </div>
            <div className="cap-bar">
              <div
                className="cap-bar-used"
                style={{ width: `${Math.min(salaryData.capPercentage, 100)}%` }}
              ></div>
            </div>
            <div className="cap-bar-info">
              <span>${salaryData.totalAnnual.toFixed(1)}M used</span>
              <span>${Math.max(0, salaryData.SALARY_CAP - salaryData.totalAnnual).toFixed(1)}M remaining</span>
            </div>
          </div>

          {/* Players Table */}
          <div className="salary-table-wrapper">
            <table className="salary-table">
              <thead>
                <tr>
                  <th className="col-rank">#</th>
                  <th className="col-name">Player</th>
                  <th className="col-pos">Pos</th>
                  <th className="col-contract">Contract</th>
                  <th className="col-total">Total</th>
                </tr>
              </thead>
              <tbody>
                {salaryData.players.map((player, index) => (
                  <tr key={index} className={index % 2 === 0 ? "even" : "odd"}>
                    <td className="col-rank">{index + 1}</td>
                    <td className="col-name">
                      <strong>{player.name}</strong>
                    </td>
                    <td className="col-pos">{player.position}</td>
                    <td className="col-contract">
                      <span className="contract-badge">
                        {player.years}yr / ${player.annual.toFixed(1)}M
                      </span>
                    </td>
                    <td className="col-total">${player.total.toFixed(1)}M</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
